import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, delay, mergeMap, retryWhen} from 'rxjs/operators';
import {Injectable, isDevMode} from '@angular/core';
import * as Sentry from '@sentry/angular';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

  private static DEFAULT_ERROR_MESSAGE = 'Could not connect to the server';
  private static DEFAULT_MAX_RETRIES = 0;
  private static DEFAULT_BACKOFF_MS = 1000;

  static getErrorMessage(errorMessage: string): string {
    return isDevMode() ? errorMessage : HttpErrorInterceptor.DEFAULT_ERROR_MESSAGE;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const retryCount = request.method === 'GET' ? 2 : 0;
    return next.handle(request)
      .pipe(
        this.retryWithBackoff(1000, retryCount),
        catchError((error: HttpErrorResponse) => this.handleErrorNew(error))
      );
  }

  private handleErrorNew(error: HttpErrorResponse) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    // Preserve 4xx errors so subscribers can handle them with actual context
    // (e.g. 400 validation mapped to form controls, 403 permission denied, 404 not found)
    if (this.isExpectedHttpError(error)) {
      return throwError(() => error);
    }

    // Report unexpected errors to Sentry (5xx, network errors, client-side errors)
    if (!isDevMode()) {
      Sentry.captureException(error);
    }

    return throwError(() => HttpErrorInterceptor.getErrorMessage(errorMessage));
  }

  private isExpectedHttpError(error: HttpErrorResponse): boolean {
    return error.status >= 400 && error.status < 500;
  }

  private retryWithBackoff(delayMs: number, maxRetry = HttpErrorInterceptor.DEFAULT_MAX_RETRIES, backoffMs = HttpErrorInterceptor.DEFAULT_BACKOFF_MS): (src: Observable<any>) => Observable<any> {
    let retries = maxRetry;
    return (src: Observable<any>) =>
      src.pipe(
        retryWhen((errors: Observable<any>) => errors.pipe(
          mergeMap(error => {
              // Don't retry client errors (4xx) — they won't resolve with retries
              if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
                return throwError(error);
              }
              if (retries-- > 0) {
                const backoffTime = delayMs + (maxRetry - retries) * backoffMs;
                return of(error).pipe(delay(backoffTime));
              } else {
                return throwError(error);
              }
            }
          ))));
  }
}
